import { VehicleRepository as Interface, VehicleStatusListener, VehicleStatusErrorListener, RTCRequestListener, RTCResponseListener } from 'core/repositories/VehicleRepository';
import { Vehicle } from 'core/models/vehicle';
import { createKeyedListenerManager } from 'utility/KeyedListenerManager';
import { Gear, DriveCommand } from 'core/models/drive';
import { createListenerManager } from 'infrastructure/utility/ListenerManager';
import { Project } from 'core/models/project';
import { updateVehicleStatus, driveCommandToStatusDelta } from 'infrastructure/utility/VehicleStatusUtility';
import { ignore } from 'utility/ignore';
import { CameraDevice } from 'core/models/mediaDevice';
import { Environment } from '@tier4/webauto-media-client/dist/types';
import { RTCTask } from 'core/models/rtc';

// dummy

/*
const standardCameraLayout = [
{
    'id': 4,
    'tag': Vehicle.Camera.PredefinedPosition.Front
},
{
    'id': 1,
    'tag': Vehicle.Camera.PredefinedPosition.Back
},
{
    'id': 3,
    'tag': Vehicle.Camera.PredefinedPosition.Left
},
{
    'id': 5,
    'tag': Vehicle.Camera.PredefinedPosition.Right
},
{
    'id': 2,
    'tag': Vehicle.Camera.PredefinedPosition.Interior
},
];

const dummyVehicles = {
'data': [
    {
        'id': '1',
        'name': 'Milee',
        'cameras': standardCameraLayout,
    },
    {
        'id': '2',
        'name': 'Lexus',
        'cameras': standardCameraLayout,
    },
    {
        'id': '3',
        'name': 'Estima',
        'cameras': []
    }
]
};
*/

/*
const moricoroVehicles = {
    data: [
        {
            id: '50e93620-3ffd-4a53-9b81-68aa47901f14',
            name: 'local',
            cameras: [
                {
                    id: 4,
                    tag: Vehicle.Camera.PredefinedPosition.Front,
                },
                {
                    id: 1,
                    tag: Vehicle.Camera.PredefinedPosition.Back,
                },
                {
                    id: 4,
                    tag: Vehicle.Camera.PredefinedPosition.Interior,
                },
                {
                    id: 3,
                    tag: Vehicle.Camera.PredefinedPosition.Left,
                },
                {
                    id: 5,
                    tag: Vehicle.Camera.PredefinedPosition.Right,
                },
            ],
        }
    ]
};
*/

const dummyCameras_: CameraDevice[] = [
    {
        type: 'camera',
        id: 'A74C3864-C943-45E7-895C-2F1D38EF25C8',
        tag: Vehicle.PredefinedCameraPosition.Front,
    },
    {
        type: 'camera',
        id: '2',
        tag: Vehicle.PredefinedCameraPosition.Back,
    },
    {
        type: 'camera',
        id: '1',
        tag: Vehicle.PredefinedCameraPosition.Interior,
    },
    {
        type: 'camera',
        id: '1',
        tag: Vehicle.PredefinedCameraPosition.Left,
    },
    {
        type: 'camera',
        id: '2',
        tag: Vehicle.PredefinedCameraPosition.Right,
    },
];

const dummyVehicles_ = {
    data: [
        {
            id: '4',
            name: 'local',
            cameras: dummyCameras_,
            microphones: [],
        }
    ]
};

function repeat(v: Vehicle, n: number) {
    const tmp = [];
    for (let i = 0; i < n; ++i) {
        tmp.push({ ...v, name: `${v.name} ${i + 1}` });
    }
    return tmp;
}

const dummyVehicles = { data: repeat(dummyVehicles_.data[0], 1) };

function update(currentStatus: Vehicle.Status, command: Partial<DriveCommand>): Vehicle.Status {
    return updateVehicleStatus(currentStatus, driveCommandToStatusDelta(command));
}

class VehicleRepository implements Interface {
    async getVehicles(): Promise<Vehicle[]> {
        return dummyVehicles.data;
    }

    async getVehicle(id: Vehicle.Id): Promise<Vehicle> {
        const vehicle = dummyVehicles.data.find(v => v.id === id);
        if (vehicle) {
            return vehicle;
        } else {
            throw new Error('not found');
        }
    }

    async getDummyVehicle(): Promise<Vehicle> {
        return dummyVehicles_.data[0];
    }

    private readonly _dummyVehicleStatus = new Map<Vehicle.Id, Vehicle.Status>();

    async postDriveCommand(vehicleId: Vehicle.Id, projectId: Project.Id, environmentId: Environment.Id, command: Partial<DriveCommand>): Promise<void> {
        ignore(projectId, environmentId);
        return new Promise(resolve => {
            setTimeout(async () => {
                const entry = this._dummyVehicleStatus.get(vehicleId);
                if (entry) {
                    this._dummyVehicleStatus.set(vehicleId, update(entry, command));
                } else {
                    this._dummyVehicleStatus.set(vehicleId, update(await this._createRandomVehicleStatus(), command));
                }
                resolve();
            }, 300);
        });
    }

    async cancelPostDriveCommand(vehicleId: Vehicle.Id): Promise<void> {
        console.log(`cancel posting vehicle status update for ${vehicleId} (if any)`);
        return;
    }

    postHeartbeat(vehicleId: Vehicle.Id, projectId: Project.Id): void {
        ignore(vehicleId, projectId);
    }

    addVehicleHeartbeatListener(vehicleId: string, projectId: string, listener: (timestamp: number, accept: boolean) => void): void {
        ignore(vehicleId, projectId, listener);
        throw new Error('Method not implemented.');
    }

    removeVehicleHeartbeatListener(vehicleId: string, projectId: string, listener: (timestamp: number, accept: boolean) => void): void {
        ignore(vehicleId, projectId, listener);
        throw new Error('Method not implemented.');
    }

    addVehicleStatusListener(vehicleId: Vehicle.Id, projectId: Project.Id, listener: VehicleStatusListener): void {
        this._statusListeners.addListener([vehicleId, projectId], listener);
    }

    removeVehicleStatusListener(vehicleId: Vehicle.Id, projectId: Project.Id, listener: VehicleStatusListener): void {
        this._statusListeners.removeListener([vehicleId, projectId], listener);
    }

    private _statusListeners = createKeyedListenerManager<[Vehicle.Id, Project.Id], VehicleStatusListener>((idPair, listeners) => {
        const [vehicleId] = idPair;

        const interval = setInterval(async () => {
            const vehicleInfo =
                this._dummyVehicleStatus.get(vehicleId)
                || await this._createInitialVehicleStatus();

            [...listeners].forEach(listener => {
                listener(vehicleInfo);
            });
        }, 1000);

        return () => {
            clearInterval(interval);
        };
    });

    private async _createInitialVehicleStatus(): Promise<Vehicle.Status> {
        const steering = 0;
        const targetSpeed = 0;
        const currentSpeed = 0;
        const accel = 0;
        const brake = 0;
        const gear = Gear.P;

        return {
            steering,
            currentSpeed,
            targetSpeed,
            stateMessage: 'running',
            accel,
            brake,
            gear,
            blinker: null,
        };
    }

    private async _createRandomVehicleStatus(): Promise<Vehicle.Status> {
        const steering = Math.random() * 2 - 1;
        const targetSpeed = Math.random() * 100;
        const currentSpeed = Math.random() * targetSpeed;
        const accel = Math.random();
        const brake = Math.random();
        const gear = [Gear.D, Gear.P, Gear.N, Gear.R][Math.min(Math.floor(Math.random() * 4), 3)];

        return {
            steering,
            currentSpeed,
            targetSpeed,
            stateMessage: 'running',
            accel,
            brake,
            gear,
            blinker: null,
        };
    }

    private readonly errorListenerManager = createListenerManager<VehicleStatusErrorListener>();

    addVehicleStatusErrorListener(listener: VehicleStatusErrorListener): void {
        this.errorListenerManager.addListener(listener);
    }

    removeVehicleStatusErrorListener(listener: VehicleStatusErrorListener): void {
        this.errorListenerManager.removeListener(listener);
    }

    private readonly rtcListenerManager = createKeyedListenerManager<string, RTCRequestListener>();

    addRTCRequestListener(vehicleId: string, projectId: string, listener: RTCRequestListener): void {
        this.rtcListenerManager.addListener(`${projectId}/${vehicleId}`, listener);
    }

    removeRTCRequestListener(vehicleId: string, projectId: string, listener: RTCRequestListener): void {
        this.rtcListenerManager.removeListener(`${projectId}/${vehicleId}`, listener);
    }

    // eslint-disable-next-line @typescript-eslint/no-unused-vars
    addRTCCommandResponseListener(_vehicleId: string, _projectId: string, _listener: RTCResponseListener): void {
        throw new Error('Method not implemented.');
    }

    // eslint-disable-next-line @typescript-eslint/no-unused-vars
    removeRTCCommandResponseListener(_vehicleId: string, _projectId: string, _listener: RTCResponseListener): void {
        throw new Error('Method not implemented.');
    }

    postRTCCommand(vehicleId: string, projectId: string, environmentId: string, task: RTCTask, activated: boolean): Promise<void> {
        ignore(vehicleId, projectId, environmentId, task, activated);
        throw new Error('Method not implemented.');
    }
}

export { VehicleRepository };
